// Required Vite env vars baked in at build time from `frontend/.env.<mode>`.
// If ANY of these are `undefined` the wizard cannot function — typically
// because `vite build` was invoked WITHOUT `--mode {dev,prod}`, in which case
// Vite defaults to mode `production`, looks for `.env.production` (which we
// don't ship — we use `.env.dev` / `.env.prod`), finds no env file, and
// silently substitutes `undefined` into every `import.meta.env.VITE_*`
// reference. The OAuth flow then sends `client_id=undefined` to Genesys and
// dies with a generic "OAuth client ID or redirect URI is invalid" page,
// which is hard to trace back to the actual root cause.
//
// Always deploy via `npm run deploy:{dev,prod}` (which calls
// `scripts/build.sh {dev,prod}` → `vite build --mode {dev,prod}`), or if
// invoking `cdk deploy` directly, run `bash scripts/build.sh <env>` first.
//
// This module-load-time assertion makes the failure mode loud and obvious:
// the wizard fails to boot with a clear error in the browser console
// instead of looking healthy until the user clicks "Start Installation".
const requiredEnv = {
  VITE_GENESYS_CLIENT_ID: import.meta.env.VITE_GENESYS_CLIENT_ID,
  VITE_WIZARD_URI_BASE: import.meta.env.VITE_WIZARD_URI_BASE,
  VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
  VITE_PREMIUM_APP_INTEGRATION_TYPE_ID: import.meta.env.VITE_PREMIUM_APP_INTEGRATION_TYPE_ID,
};
const missingEnv = Object.entries(requiredEnv).filter(([, v]) => !v).map(([k]) => k);
if (missingEnv.length > 0) {
  throw new Error(
    `Wizard configuration error: required env vars are undefined in this build: ${missingEnv.join(', ')}. ` +
    `This usually means \`vite build\` ran without a \`--mode {dev,prod}\` flag and could not find a matching \`.env.<mode>\` file. ` +
    `Use \`npm run deploy:{dev,prod}\` for deploys, or \`bash scripts/build.sh {dev,prod}\` for manual builds.`,
  );
}

// Mediabriz Insights dashboard URL — hardcoded by design per `docs/INSIGHTS_DASHBOARD_WORKPLAN.md`
// §7.4 ("Both `https://insights.mediabriz.com` (in wizard) and `https://wizard.mediabriz.com`
// (in insights) are hardcoded production URLs. They're stable, low-blast-radius, and one less
// env-var moving part. If we ever rename either domain we touch both repos in a coordinated
// release — acceptable cost."). Single-stack SPA (S3 + CloudFront in us-east-1) — no per-env
// variant; the auth gate is per-user, not per-environment, so the prod URL works for wizard
// dev testing too. Trailing slash matters — the redirect URL is constructed as
// `${INSIGHTS_URL}?<forwardedParams>`, not `${INSIGHTS_URL}/?<…>`.
export const INSIGHTS_URL = 'https://insights.mediabriz.com/';

// "Mediabriz extension installation guide" URL surfaced on the Setup Complete
// page's step-3 "Mediabriz documentation" link (not on the Landing Page footer).
// Uses the customer-facing `support.mediabriz.com` origin for consistency with every other
// support link in the wizard frontend (LandingPage general-support link, ErrorPage missing-
// permissions support-center link, SetupCompletePage step-3 documentation link). The article
// itself is served by Freshdesk's KB under the hood; `support.mediabriz.com` is the customer-
// facing CNAME that fronts the same content with a cleaner URL. (Previous value pointed at
// `mediabriz.freshdesk.com/...` — the raw Freshdesk host — which worked but created a domain
// mismatch with the rest of the wizard's support links; unified to `support.mediabriz.com` so
// users see a single support origin across the install flow.) Note the URL slug
// `extention-install-guide` carries a typo from the KB article id and is preserved verbatim —
// fixing it client-side would silently break the link.
export const INSTALL_HELP_URL = 'https://support.mediabriz.com/support/solutions/articles/158000451027-extention-install-guide';

// Conversation Actions support articles (Conversation Actions page).
// Same `support.mediabriz.com` origin as INSTALL_HELP_URL (no `/en/` prefix —
// Freshdesk redirects `/en/...` to the non-locale form). Catalog article covers
// CSV prepare/upload; webhook article covers headers, HMAC signature, and payload
// fields. The agent-facing overview article ("How can I create an order from a
// customer conversation") is intentionally NOT linked from Settings — Settings
// is admin setup; that article is workflow-oriented.
export const ACTIONS_CATALOG_HELP_URL =
  'https://support.mediabriz.com/support/solutions/articles/158000464639-how-do-i-prepare-and-upload-a-product-catalog-for-create-order-';
export const ACTIONS_WEBHOOK_HELP_URL =
  'https://support.mediabriz.com/support/solutions/articles/158000464641-how-do-i-integrate-create-order-with-my-organization-s-system';
// Send Files to CRM setup article. Shown on the type description, not on the
// sample-payload note at the bottom of the card.
export const SEND_FILES_HELP_URL =
  'https://support.mediabriz.com/support/solutions/articles/158000471804-how-can-i-send-customer-files-to-my-crm';
// Overview of every action type; the one guide link in the Add action dialog.
export const ACTIONS_GUIDE_URL =
  'https://support.mediabriz.com/support/solutions/articles/158000472828-about-conversation-actions';
// Login Limit admin article, linked under the Login Limit intro in Settings.
// An empty string hides the link.
export const LOGIN_LIMIT_HELP_URL =
  'https://support.mediabriz.com/support/solutions/articles/158000476712-how-do-i-manage-genesys-cloud-license-use-with-license-manager-';

export const wizardConfig = {
  clientID: import.meta.env.VITE_GENESYS_CLIENT_ID,
  wizardUriBase: import.meta.env.VITE_WIZARD_URI_BASE,
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL,

  premiumAppIntegrationTypeId: import.meta.env.VITE_PREMIUM_APP_INTEGRATION_TYPE_ID,
  // Display names in Genesys: "cloudBriz - View" and "cloudBriz - Admin".
  // View was verified against a live org (May 2026). Admin uses that same
  // integration:<entity>:<action> id.
  premiumAppViewPermission: 'integration:cloudBriz:view',
  premiumAppAdminPermission: 'integration:cloudBriz:admin',

  prefix: 'Mediabriz ',

  defaultGcEnvironment: 'mypurecloud.com',
  defaultLanguage: 'en-us',

  enableCustomSetupPageBeforeInstall: true,
  enableCustomSetupStepAfterInstall: true,
  enableDynamicInstallSummary: true,
  enableUninstall: import.meta.env.VITE_ENABLE_UNINSTALL === 'true',

  // Permission check mode for the install wizard.
  // 'wizard' = verify admin has permissions to create roles/OAuth clients (recommended).
  // 'all' = also requires premiumAppViewPermission (admin must have assigned it to themselves first).
  // 'none' = skip all permission checks (Genesys default).
  checkInstallPermissions: 'wizard' as string,

  installPermissions: {
    custom: [],
    wizard: ['integration:cloudBriz:admin'],
    postCustomSetup: [],
    'oauth-client': ['oauth:client:view', 'oauth:client:add', 'oauth:client:edit'],
  },

  uninstallPermissions: {
    custom: [],
    wizard: [],
    postCustomSetup: [],
    role: ['authorization:role:delete'],
    'oauth-client': ['oauth:client:edit', 'oauth:client:delete'],
  },

  // Genesys Cloud URL params
  languageQueryParam: 'langTag',
  genesysCloudHostOriginQueryParam: 'hostOrigin',
  genesysCloudTargetEnvQueryParam: 'targetEnv',
  genesysCloudEnvironmentQueryParam: 'environment',

  // Provisioning configuration
  provisioningInfo: {
    role: {
      name: 'App',
      description: 'Allows access to the Mediabriz application',
      permissionPolicies: [
        {
          domain: 'integration',
          entityName: 'cloudBriz',
          actionSet: ['*'],
          allowConditions: false,
        },
      ],
    },
    oauthClient: {
      name: 'App',
      description: 'Mediabriz OAuth client for browser extension user authentication (PKCE).',
      authorizedGrantType: 'CODE',
      accessTokenValiditySeconds: 43200,
      registeredRedirectUri: [
        // Published Mediabriz browser extensions. The chrome.identity.launchWebAuthFlow
        // callback URI for chromium-based browsers is https://<extension-id>.chromiumapp.org/.
        // Both Chrome and Edge use the same scheme (Edge surfaces this id as the "CRX ID"
        // in the partner dashboard).
        'https://dojbgeididmgnlieckgekbfdncaamhbm.chromiumapp.org/', // Chrome Web Store: Mediabriz
        'https://bpdjeihgofhpojeghkomckhgahioiomo.chromiumapp.org/', // Microsoft Edge Add-ons: Mediabriz
      ],
      scope: [
        'conversations',
        'conversations:readonly',
        'organization:readonly',
        'user-basic-info',
        'notifications',
        'analytics:readonly',
        'external-contacts:readonly',
      ],
    },
  },
};

/**
 * Genesys Cloud region to Mediabriz data region mapping.
 * European regions map to EU, everything else defaults to US.
 */
export const regionToDataRegion: Record<string, string> = {
  'mypurecloud.ie': 'EU',
  'euw2.pure.cloud': 'EU',
  'mypurecloud.de': 'EU',
  'euc2.pure.cloud': 'EU',
  'edee1.eusc-pure.cloud': 'EU', // European Sovereign (Brandenburg) — eusc-de-east-1
};

export function getDataRegion(gcEnvironment: string): string {
  return regionToDataRegion[gcEnvironment] || 'US';
}

/**
 * Sanitize org name for registration: lowercase, replace spaces with hyphens,
 * remove characters not in [a-z0-9_-].
 */
export function sanitizeOrgName(orgName: string): string {
  return orgName
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9_-]/g, '');
}
